import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  countryCode,
  PagesFormField,
  SmallGrid,
} from '@xing-com/crate-entity-pages-common';

import type { LocationStateType } from '../../types';
import {
  addParenthisisOnNumbers,
  removeParenthisisOnNumbers,
  removeWhiteSpaces,
  addHttpToWebsiteUrl,
} from '../../utils/location-form';
import { locationValidation } from '../../utils/location-form-validation';

import * as Styled from './location-form.styles';

type ListType = {
  value: string;
  text: string;
}[];

const countryTranslationKeys = {
  AF: 'COUNTRY_AF',
  AL: 'COUNTRY_AL',
  DZ: 'COUNTRY_DZ',
  AS: 'COUNTRY_AS',
  AD: 'COUNTRY_AD',
  AO: 'COUNTRY_AO',
  AI: 'COUNTRY_AI',
  AQ: 'COUNTRY_AQ',
  BV: 'COUNTRY_BV',
  HM: 'COUNTRY_HM',
  TF: 'COUNTRY_TF',
  UM: 'COUNTRY_UM',
  EH: 'COUNTRY_EH',
  AG: 'COUNTRY_AG',
  AR: 'COUNTRY_AR',
  AM: 'COUNTRY_AM',
  AW: 'COUNTRY_AW',
  AZ: 'COUNTRY_AZ',
  AU: 'COUNTRY_AU',
  BS: 'COUNTRY_BS',
  BH: 'COUNTRY_BH',
  BD: 'COUNTRY_BD',
  BB: 'COUNTRY_BB',
  BE: 'COUNTRY_BE',
  BZ: 'COUNTRY_BZ',
  BJ: 'COUNTRY_BJ',
  BM: 'COUNTRY_BM',
  BT: 'COUNTRY_BT',
  BO: 'COUNTRY_BO',
  BA: 'COUNTRY_BA',
  BW: 'COUNTRY_BW',
  BR: 'COUNTRY_BR',
  IO: 'COUNTRY_IO',
  BN: 'COUNTRY_BN',
  BG: 'COUNTRY_BG',
  BF: 'COUNTRY_BF',
  MM: 'COUNTRY_MM',
  BI: 'COUNTRY_BI',
  KY: 'COUNTRY_KY',
  CL: 'COUNTRY_CL',
  CN: 'COUNTRY_CN',
  CK: 'COUNTRY_CK',
  CR: 'COUNTRY_CR',
  CI: 'COUNTRY_CI',
  DE: 'COUNTRY_DE',
  CD: 'COUNTRY_CD',
  DM: 'COUNTRY_DM',
  DO: 'COUNTRY_DO',
  DJ: 'COUNTRY_DJ',
  DK: 'COUNTRY_DK',
  EC: 'COUNTRY_EC',
  SV: 'COUNTRY_SV',
  ER: 'COUNTRY_ER',
  EE: 'COUNTRY_EE',
  FK: 'COUNTRY_FK',
  FO: 'COUNTRY_FO',
  FJ: 'COUNTRY_FJ',
  FI: 'COUNTRY_FI',
  FR: 'COUNTRY_FR',
  GF: 'COUNTRY_GF',
  PF: 'COUNTRY_PF',
  GA: 'COUNTRY_GA',
  GM: 'COUNTRY_GM',
  GE: 'COUNTRY_GE',
  GH: 'COUNTRY_GH',
  GI: 'COUNTRY_GI',
  GD: 'COUNTRY_GD',
  GR: 'COUNTRY_GR',
  GL: 'COUNTRY_GL',
  GP: 'COUNTRY_GP',
  GU: 'COUNTRY_GU',
  GT: 'COUNTRY_GT',
  GG: 'COUNTRY_GG',
  GN: 'COUNTRY_GN',
  GW: 'COUNTRY_GW',
  GY: 'COUNTRY_GY',
  HT: 'COUNTRY_HT',
  HN: 'COUNTRY_HN',
  HK: 'COUNTRY_HK',
  IN: 'COUNTRY_IN',
  ID: 'COUNTRY_ID',
  IQ: 'COUNTRY_IQ',
  IR: 'COUNTRY_IR',
  IE: 'COUNTRY_IE',
  IS: 'COUNTRY_IS',
  IM: 'COUNTRY_IM',
  IL: 'COUNTRY_IL',
  IT: 'COUNTRY_IT',
  JM: 'COUNTRY_JM',
  JP: 'COUNTRY_JP',
  YE: 'COUNTRY_YE',
  JE: 'COUNTRY_JE',
  JO: 'COUNTRY_JO',
  VG: 'COUNTRY_VG',
  VI: 'COUNTRY_VI',
  KH: 'COUNTRY_KH',
  CM: 'COUNTRY_CM',
  CA: 'COUNTRY_CA',
  CV: 'COUNTRY_CV',
  LC: 'COUNTRY_LC',
  KZ: 'COUNTRY_KZ',
  QA: 'COUNTRY_QA',
  KE: 'COUNTRY_KE',
  KG: 'COUNTRY_KG',
  KI: 'COUNTRY_KI',
  CC: 'COUNTRY_CC',
  CO: 'COUNTRY_CO',
  KM: 'COUNTRY_KM',
  CG: 'COUNTRY_CG',
  HR: 'COUNTRY_HR',
  CU: 'COUNTRY_CU',
  KW: 'COUNTRY_KW',
  LA: 'COUNTRY_LA',
  LS: 'COUNTRY_LS',
  LV: 'COUNTRY_LV',
  LB: 'COUNTRY_LB',
  LR: 'COUNTRY_LR',
  LY: 'COUNTRY_LY',
  LI: 'COUNTRY_LI',
  LT: 'COUNTRY_LT',
  LU: 'COUNTRY_LU',
  MO: 'COUNTRY_MO',
  MG: 'COUNTRY_MG',
  MW: 'COUNTRY_MW',
  MY: 'COUNTRY_MY',
  MV: 'COUNTRY_MV',
  ML: 'COUNTRY_ML',
  MT: 'COUNTRY_MT',
  MA: 'COUNTRY_MA',
  MH: 'COUNTRY_MH',
  MQ: 'COUNTRY_MQ',
  MR: 'COUNTRY_MR',
  MU: 'COUNTRY_MU',
  YT: 'COUNTRY_YT',
  MK: 'COUNTRY_MK',
  MX: 'COUNTRY_MX',
  FM: 'COUNTRY_FM',
  MD: 'COUNTRY_MD',
  MC: 'COUNTRY_MC',
  MN: 'COUNTRY_MN',
  ME: 'COUNTRY_ME',
  MS: 'COUNTRY_MS',
  MZ: 'COUNTRY_MZ',
  NA: 'COUNTRY_NA',
  NR: 'COUNTRY_NR',
  NP: 'COUNTRY_NP',
  NC: 'COUNTRY_NC',
  NZ: 'COUNTRY_NZ',
  NI: 'COUNTRY_NI',
  NL: 'COUNTRY_NL',
  AN: 'COUNTRY_AN',
  NE: 'COUNTRY_NE',
  NG: 'COUNTRY_NG',
  NU: 'COUNTRY_NU',
  KP: 'COUNTRY_KP',
  NF: 'COUNTRY_NF',
  NO: 'COUNTRY_NO',
  MP: 'COUNTRY_MP',
  OM: 'COUNTRY_OM',
  TL: 'COUNTRY_TL',
  PK: 'COUNTRY_PK',
  PW: 'COUNTRY_PW',
  PS: 'COUNTRY_PS',
  PA: 'COUNTRY_PA',
  PG: 'COUNTRY_PG',
  PY: 'COUNTRY_PY',
  PE: 'COUNTRY_PE',
  PH: 'COUNTRY_PH',
  PN: 'COUNTRY_PN',
  PL: 'COUNTRY_PL',
  PT: 'COUNTRY_PT',
  PR: 'COUNTRY_PR',
  RE: 'COUNTRY_RE',
  RW: 'COUNTRY_RW',
  RO: 'COUNTRY_RO',
  RU: 'COUNTRY_RU',
  KN: 'COUNTRY_KN',
  PM: 'COUNTRY_PM',
  VC: 'COUNTRY_VC',
  SB: 'COUNTRY_SB',
  WS: 'COUNTRY_WS',
  SM: 'COUNTRY_SM',
  SH: 'COUNTRY_SH',
  ST: 'COUNTRY_ST',
  SA: 'COUNTRY_SA',
  SE: 'COUNTRY_SE',
  CH: 'COUNTRY_CH',
  SN: 'COUNTRY_SN',
  RS: 'COUNTRY_RS',
  SC: 'COUNTRY_SC',
  SL: 'COUNTRY_SL',
  SG: 'COUNTRY_SG',
  SK: 'COUNTRY_SK',
  SI: 'COUNTRY_SI',
  SO: 'COUNTRY_SO',
  ES: 'COUNTRY_ES',
  LK: 'COUNTRY_LK',
  SD: 'COUNTRY_SD',
  SR: 'COUNTRY_SR',
  SJ: 'COUNTRY_SJ',
  SZ: 'COUNTRY_SZ',
  SY: 'COUNTRY_SY',
  ZA: 'COUNTRY_ZA',
  GS: 'COUNTRY_GS',
  KR: 'COUNTRY_KR',
  TJ: 'COUNTRY_TJ',
  TW: 'COUNTRY_TW',
  TZ: 'COUNTRY_TZ',
  TH: 'COUNTRY_TH',
  TG: 'COUNTRY_TG',
  TK: 'COUNTRY_TK',
  TO: 'COUNTRY_TO',
  TT: 'COUNTRY_TT',
  TD: 'COUNTRY_TD',
  CZ: 'COUNTRY_CZ',
  TN: 'COUNTRY_TN',
  TM: 'COUNTRY_TM',
  TC: 'COUNTRY_TC',
  TV: 'COUNTRY_TV',
  TR: 'COUNTRY_TR',
  UG: 'COUNTRY_UG',
  UA: 'COUNTRY_UA',
  HU: 'COUNTRY_HU',
  UY: 'COUNTRY_UY',
  UZ: 'COUNTRY_UZ',
  VU: 'COUNTRY_VU',
  VA: 'COUNTRY_VA',
  VE: 'COUNTRY_VE',
  AE: 'COUNTRY_AE',
  US: 'COUNTRY_US',
  GB: 'COUNTRY_GB',
  VN: 'COUNTRY_VN',
  WF: 'COUNTRY_WF',
  CX: 'COUNTRY_CX',
  BY: 'COUNTRY_BY',
  ZM: 'COUNTRY_ZM',
  CF: 'COUNTRY_CF',
  ZW: 'COUNTRY_ZW',
  CY: 'COUNTRY_CY',
  EG: 'COUNTRY_EG',
  AX: 'COUNTRY_AX',
  GQ: 'COUNTRY_GQ',
  ET: 'COUNTRY_ET',
  AT: 'COUNTRY_AT',
} as const;

type LocationProps = {
  locationState: LocationStateType;
  setLocationState?: (locationState: any) => void;
  setLocationsTouched?: (locationTouched: any) => void;
};
export const LocationForm: FC<LocationProps> = ({
  locationState,
  setLocationState = () => undefined,
  setLocationsTouched = () => undefined,
}) => {
  const intl = useIntl();
  const [orderedByLanguageCountryList, setOrderedByLanguageCountryList] =
    useState<ListType>([{ value: '', text: '' }]);
  const [countryCodeObj, setCountryCodeObj] = useState<ListType>([
    { value: '', text: '' },
  ]);
  const commonFields = {
    validate: locationValidation,
    formState: locationState,
    setFormState: setLocationState,
    setFormTouched: setLocationsTouched,
    copyPrefix: 'EP_EDIT_LOCATION_',
  };

  useEffect(() => {
    setOrderedByLanguageCountryList(
      Object.keys(countryCode)
        .map((code) => ({
          value: code,
          text: intl.formatMessage({
            id: countryTranslationKeys[
              code as keyof typeof countryTranslationKeys
            ],
            defaultMessage:
              countryTranslationKeys[
                code as keyof typeof countryTranslationKeys
              ],
          }),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    );

    const countryCodeObj = new Array(...Object.values(countryCode))
      .sort((a, b) => a - b)
      .map((countryCode) => ({
        value: countryCode,
        text: `+${countryCode}`,
      }));
    setCountryCodeObj(countryCodeObj);
  }, [intl.locale]);

  return (
    <SmallGrid inner>
      <PagesFormField
        type="label"
        {...commonFields}
        required
        data-testid="CONTACT_FIELD_LABEL"
      />
      <PagesFormField
        type="address"
        {...commonFields}
        required
        data-testid="CONTACT_FIELD_ADDRESS"
      />
      <PagesFormField
        type="addressSuffix"
        {...commonFields}
        data-testid="CONTACT_FIELD_SUFFIX"
      />
      <Styled.FieldGroup>
        <Styled.PagesFormFieldZipcode
          type="postcode"
          applyToValue={(value: string) =>
            removeWhiteSpaces(value).toUpperCase()
          }
          required
          {...commonFields}
          data-testid="CONTACT_FIELD_POSTCODE"
        />
        <Styled.PagesFormFieldCity
          type="city"
          required
          {...commonFields}
          data-testid="CONTACT_FIELD_CITY"
        />
      </Styled.FieldGroup>
      <PagesFormField
        type="country"
        {...commonFields}
        optionList={orderedByLanguageCountryList}
        defaultValue={'DE'}
        data-testid="CONTACT_FIELD_COUNTRY"
      />
      <Styled.FieldGroup>
        <Styled.PagesFormFieldPhonePrefix
          type="phoneNumberCountryCode"
          optionList={countryCodeObj}
          defaultValue={49}
          {...commonFields}
          data-testid="CONTACT_FIELD_PHONECODE"
        />
        <Styled.PagesFormFieldPhone
          type="phoneNumber"
          applyToValue={(value: string) => removeWhiteSpaces(value)}
          applyToValueOnBlur={(value: string) => addParenthisisOnNumbers(value)}
          applyToValueOnFocus={(value: string) =>
            removeParenthisisOnNumbers(value)
          }
          {...commonFields}
          data-testid="CONTACT_FIELD_PHONENUMBER"
        />
        <Styled.PagesFormFieldFaxPrefix
          type="faxNumberCountryCode"
          optionList={countryCodeObj}
          defaultValue={49}
          {...commonFields}
          data-testid="CONTACT_FIELD_FAXCODE"
        />
        <Styled.PagesFormFieldFax
          type="faxNumber"
          applyToValue={(value: string) => removeWhiteSpaces(value)}
          applyToValueOnBlur={(value: string) => addParenthisisOnNumbers(value)}
          applyToValueOnFocus={(value: string) =>
            removeParenthisisOnNumbers(value)
          }
          {...commonFields}
          data-testid="CONTACT_FIELD_FAX"
        />
      </Styled.FieldGroup>
      <PagesFormField
        type="email"
        {...commonFields}
        applyToValue={(value: string) => removeWhiteSpaces(value)}
        data-testid="CONTACT_FIELD_EMAIL"
      />
      <PagesFormField
        type="websiteURL"
        {...commonFields}
        applyToValue={(value: string) => removeWhiteSpaces(value)}
        applyToValueOnBlur={(value: string) => addHttpToWebsiteUrl(value)}
        data-testid="CONTACT_FIELD_WEBSITEURL"
      />
    </SmallGrid>
  );
};
